import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  api,
  clearTokens,
  loadRefreshToken,
  refreshTokensFromStorage,
  setTokens,
} from "@/api/client";
import type {
  AccountType,
  AuthUser,
  LoginRequest,
  RegisterOrgRequest,
  RegisterRequest,
  TokenResponse,
} from "@/api/types";

interface RegisterOrgArgs {
  email: string;
  password: string;
  displayName: string;
  orgName: string;
  orgSlug: string;
}

interface AuthContextValue {
  user: AuthUser | null;
  /** True until initial refresh + /me (or anonymous decision) completes. */
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, accountType?: AccountType) => Promise<void>;
  register: (
    email: string,
    password: string,
    displayName: string,
    accountType?: AccountType,
  ) => Promise<void>;
  registerOrganization: (args: RegisterOrgArgs) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function fetchAndSetMe(setUser: (u: AuthUser | null) => void): Promise<void> {
  const me = await api.get<AuthUser>("/api/v1/auth/me");
  setUser(me);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      const stored = loadRefreshToken();
      if (!stored) {
        if (!cancelled) setIsLoading(false);
        return;
      }

      const refreshed = await refreshTokensFromStorage();
      if (cancelled) return;
      if (!refreshed) {
        setUser(null);
        setIsLoading(false);
        return;
      }

      try {
        const me = await api.get<AuthUser>("/api/v1/auth/me");
        if (cancelled) return;
        setUser(me);
      } catch {
        if (!cancelled) {
          clearTokens();
          setUser(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(
    async (email: string, password: string, accountType?: AccountType) => {
      // Backend's AuthUserResponse nests tokens under a `tokens` key
      // (qm-api/app/api/contracts/auth.py). The /auth/me round-trip below
      // populates user per spec §15.4.
      const body: LoginRequest = { email, password };
      if (accountType) body.account_type = accountType;
      const response = await api.post<{ tokens: TokenResponse }>(
        "/api/v1/auth/login",
        body,
      );
      setTokens(response.tokens.access_token, response.tokens.refresh_token);
      await fetchAndSetMe(setUser);
    },
    [],
  );

  const register = useCallback(
    async (
      email: string,
      password: string,
      displayName: string,
      accountType?: AccountType,
    ) => {
      // Same nested `tokens` shape as /auth/login above.
      const body: RegisterRequest = { email, password, display_name: displayName };
      if (accountType) body.account_type = accountType;
      const response = await api.post<{ tokens: TokenResponse }>(
        "/api/v1/auth/register",
        body,
      );
      setTokens(response.tokens.access_token, response.tokens.refresh_token);
      await fetchAndSetMe(setUser);
    },
    [],
  );

  const registerOrganization = useCallback(
    async ({ email, password, displayName, orgName, orgSlug }: RegisterOrgArgs) => {
      // Atomic user + org creation; user lands as org_admin of the new org.
      const body: RegisterOrgRequest = {
        email,
        password,
        display_name: displayName,
        org_name: orgName,
        org_slug: orgSlug,
      };
      const response = await api.post<{ tokens: TokenResponse }>(
        "/api/v1/auth/register-organization",
        body,
      );
      setTokens(response.tokens.access_token, response.tokens.refresh_token);
      await fetchAndSetMe(setUser);
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/api/v1/auth/logout");
    } catch {
      // Still clear session locally if the server is unreachable or session already invalid.
    } finally {
      clearTokens();
      setUser(null);
      // Auth wraps RouterProvider, so TanStack navigate hooks are unavailable here.
      if (typeof window !== "undefined") window.location.assign("/");
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: user !== null,
      login,
      register,
      registerOrganization,
      logout,
    }),
    [user, isLoading, login, register, registerOrganization, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
